import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import CoproModal from './CoproModal';

interface Copro {
  id: string;
  numero: string;
  residence: string;
}

type CoproType = 'lefeuvre' | 'newco' | 'syndic';

interface CoproSettingsProps {
  type?: CoproType;
}

export default function CoproSettings({ type = 'lefeuvre' }: CoproSettingsProps) {
  const [copros, setCopros] = useState<Copro[]>([]);
  const [filteredCopros, setFilteredCopros] = useState<Copro[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCopro, setSelectedCopro] = useState<Copro | null>(null);
  const [importing, setImporting] = useState(false);

  const getTableName = (): string => {
    switch (type) {
      case 'lefeuvre': return 'copros';
      case 'newco': return 'copros_newco';
      case 'syndic': return 'copros_syndic';
    }
  };

  const getTitle = (): string => {
    switch (type) {
      case 'lefeuvre': return 'Copropriétés Lefeuvre';
      case 'newco': return 'Copropriétés NewCo';
      case 'syndic': return 'Copropriétés Kermarrec Syndic';
    }
  };

  useEffect(() => {
    loadData();
  }, [type]);

  useEffect(() => {
    filterCopros();
  }, [searchTerm, copros]);

  const loadData = async () => {
    try {
      const tableName = getTableName();
      const { data, error: coprosError } = await supabase
        .from(tableName)
        .select('*')
        .order('numero');

      if (coprosError) throw coprosError;
      setCopros(data || []);
      setFilteredCopros(data || []);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filterCopros = () => {
    const filtered = copros.filter(copro => {
      const searchLower = searchTerm.toLowerCase();
      return copro.numero.toLowerCase().includes(searchLower) ||
             copro.residence.toLowerCase().includes(searchLower);
    });
    setFilteredCopros(filtered);
  };

  const handleEditClick = (copro: Copro) => {
    setSelectedCopro(copro);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedCopro(null);
    setIsModalOpen(true);
  };

  const handleDeleteCopro = async (copro: Copro) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la copropriété ${copro.numero} ?`)) {
      return;
    }

    try {
      const tableName = getTableName();
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', copro.id);

      if (error) throw error;
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setError('');

      const text = await file.text();
      const rows = text.split('\n');
      
      // Skip header row and filter out empty rows
      const data = rows.slice(1)
        .filter(row => row.trim())
        .map(row => {
          const [numero, residence] = row.split(';').map(field => field.trim());
          return { numero, residence };
        });

      const tableName = getTableName();
      const { error } = await supabase
        .from(tableName)
        .insert(data);

      if (error) throw error;

      loadData();
      alert('Import terminé avec succès');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'numero;residence\n123;Résidence Example';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `template_copros_${type}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher une copropriété..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id={`csv-upload-${type}`}
            />
            <button
              onClick={() => document.getElementById(`csv-upload-${type}`)?.click()}
              disabled={importing}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Upload size={20} />
              <span>{importing ? 'Import en cours...' : 'Importer CSV'}</span>
            </button>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Télécharger le modèle
          </button>
          <button
            onClick={handleAddClick}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            <span>Nouvelle copropriété</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Numéro
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Résidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCopros.map((copro) => (
              <tr key={copro.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {copro.numero}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {copro.residence}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditClick(copro)}
                      className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
                      title="Modifier la copropriété"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteCopro(copro)}
                      className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100"
                      title="Supprimer la copropriété"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCopros.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucune copropriété trouvée
          </div>
        )}
      </div>

      <CoproModal
        copro={selectedCopro}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCopro(null);
        }}
        onSuccess={loadData}
        tableName={getTableName()}
      />
    </div>
  );
}
